import { ChangeEvent, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { classNames } from 'primereact/utils';
import { CheckIconV2, CloseIcon, CopyIcon, InfoCircleIcon, InputSearch, useGlobalToast } from '@repo/atomic-components';
import { OutlineIconButton } from '@/ui/components/OutlineIconButton';
import {
  api,
  isEndpointLockError,
  LiveMedicalMatchBlockedReason,
  MEDICAL_MATCH_LIVE_STATUS_TAG,
  MedicalMatchRerunBlockedReason,
  useGetCanRerunMedicalMatchQuery,
  useGetCanRunLiveMedicalMatchQuery,
  useGetLiveMedicalMatchStalenessQuery,
  useLazyGetMedicalMatchQuery,
  useRerunMedicalMatchMutation,
  useSendLiveQuestionsMutation,
} from '@/api';
import type { MedicalMatchBreach, MedicalMatchEntry, MedicalMatchItem } from '@/api';
import { AccessControlAction } from '@/constants';
import { useAccessControl } from '@/hooks';
import {
  addVisitedCitationPageId,
  makeSelectBundlePageIndexByPageId,
  makeSelectFullBundlePageIndexByPageId,
  selectMedicalMatchData,
  selectMedicalMatchFetchedCaseId,
  selectMedicalMatchIsLoading,
  selectMedicalMatchError,
  selectMedicalMatchPanelScrollTop,
  selectMedicalMatchPanelSearchTerm,
  selectMedicalMatchVisitedCitationPageIds,
  setMedicalMatchData,
  setMedicalMatchLoading,
  setMedicalMatchError,
  setMedicalMatchHighlight,
  updateMedicalMatchPanel,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { useViewportPageIndexContext } from '../../hooks';
import { MedicalMatchEntryBlock } from './MedicalMatchEntryBlock';
import { MedicalMatchProcessingBar } from './MedicalMatchProcessingBar';
import { MedicalMatchQuestionComposer } from './MedicalMatchQuestionComposer';
import { MedicalMatchRerunButton } from './MedicalMatchRerunButton';
import { useLiveAnswerScroll } from './useLiveAnswerScroll';
import { useLiveProcessingQueue } from './useLiveProcessingQueue';
import { useLiveQuestionFailures } from './useLiveQuestionFailures';
import { LiveQuestionNotice, useLiveQuestionNotices } from './useLiveQuestionNotices';
import {
  LIVE_ANSWER_REFRESH_ATTEMPTS,
  LIVE_ANSWER_REFRESH_INTERVAL_MS,
  LIVE_ANSWER_REFRESH_RETRY_DELAY_MS,
  LIVE_MEDICAL_MATCH_NOT_ENABLED_MESSAGE,
  LIVE_MEDICAL_MATCH_RERUN_LINK_LABEL,
  LIVE_MEDICAL_MATCH_RERUN_PROMPT,
  LIVE_MEDICAL_MATCH_STALE_DOCUMENTS_PENDING_MESSAGE,
  LIVE_MEDICAL_MATCH_STALE_MESSAGE,
  LIVE_QUESTION_DAILY_LIMIT_MESSAGE,
  MEDICAL_MATCH_COPY_FAILED_DETAIL,
  MEDICAL_MATCH_COPY_FAILED_SUMMARY,
  MEDICAL_MATCH_RERUN_IN_PROGRESS_MESSAGE,
  MEDICAL_MATCH_RERUN_RETRY_DELAY_MS,
  MEDICAL_MATCH_STATUS_POLL_INTERVAL_MS,
} from './constants';
import { HighlightedText } from './HighlightedText';
import {
  getAnswerEntryIds,
  getLiveBlockedReason,
  getRerunErrorDetail,
  getRerunTooltip,
  getSendLiveQuestionErrorDetail,
  matchesSearchTerm,
  normaliseSearchTerm,
  writeToClipboard,
} from './utils';

/**
 * A section plus a stable React key. The API's groups carry no id and their
 * `title` is a formatted date range — "Unknown Dates" for the undated ones — so
 * titles repeat across allegations, and keying on the title alone had React
 * reconciling two different sections onto each other every time the search
 * re-derived the list. Keyed on title and allegation instead, numbered where
 * both repeat, and worked out over the unfiltered data: that stays put as the
 * term narrows things down, and — unlike a position — when a refresh adds a
 * section above, so cards keep their expanded state through both.
 */
type MedicalMatchSection = MedicalMatchItem & { sectionKey: string };

export function MedicalMatchPanel() {
  const { caseId } = useParams<{ caseId: string }>();
  const dispatch = useAppDispatch();
  // Live questions and re-runs go to everyone who can see Medical Match (see
  // BundleSidebarTabs). Anyone the matrix turns away gets a read-only panel,
  // and none of the checks below are worth polling.
  const canUseLiveMedicalMatch = useAccessControl(AccessControlAction.USE_LIVE_MEDICAL_MATCH);
  const data = useAppSelector(selectMedicalMatchData);
  const isLoading = useAppSelector(selectMedicalMatchIsLoading);
  const error = useAppSelector(selectMedicalMatchError);
  const [triggerGetMedicalMatch] = useLazyGetMedicalMatchQuery();
  const [sendLiveQuestions] = useSendLiveQuestionsMutation();
  const [rerunMedicalMatch, { isLoading: isStartingRerun }] = useRerunMedicalMatchMutation();
  const globalToastRef = useGlobalToast();
  const documentReviewPageContext = useViewportPageIndexContext();
  const getBundlePageIndexByPageId = useAppSelector(makeSelectBundlePageIndexByPageId);
  const getFullBundlePageIndexByPageId = useAppSelector(makeSelectFullBundlePageIndexByPageId);

  const handleAnswersRefreshFailed = useCallback(
    (error: unknown) => {
      console.error('Failed to refresh Medical Match answers:', error);
      globalToastRef.current?.show({
        severity: 'error',
        summary: 'Answers could not be refreshed',
        detail: 'Reopen Medical Match to load the latest answers.',
        closable: true,
        life: 5000,
      });
    },
    [globalToastRef],
  );

  // Read through a ref so the load below runs once per case, not again when
  // its own response lands.
  const fetchedCaseId = useAppSelector(selectMedicalMatchFetchedCaseId);
  const fetchedCaseIdRef = useRef(fetchedCaseId);
  fetchedCaseIdRef.current = fetchedCaseId;

  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;

    // The store outlives the panel, which unmounts on every sidebar tab switch.
    // Coming back to a case already in it, the answers stay up while they're
    // fetched again rather than giving way to the loading placeholder — which
    // would also throw away where the user had scrolled to — and a failure
    // leaves them there with a toast rather than swapping them for the error.
    const isRefresh = fetchedCaseIdRef.current === caseId;
    if (!isRefresh) dispatch(setMedicalMatchLoading(true));
    dispatch(setMedicalMatchError(null));

    triggerGetMedicalMatch({ caseId })
      .unwrap()
      .then((result) => {
        if (!cancelled) dispatch(setMedicalMatchData({ caseId, data: result }));
      })
      .catch((err) => {
        if (cancelled) return;
        if (isRefresh) handleAnswersRefreshFailed(err);
        else dispatch(setMedicalMatchError(err?.data?.message ?? 'Failed to load Medical Match data'));
      })
      .finally(() => {
        if (!cancelled) dispatch(setMedicalMatchLoading(false));
      });

    return () => {
      cancelled = true;
    };
  }, [caseId, dispatch, handleAnswersRefreshFailed, triggerGetMedicalMatch]);

  // Pull the answers again and push them into the store. Shared by the live
  // question flow and the rerun watcher below. Deliberately doesn't touch
  // `setMedicalMatchLoading`: that flag swaps the whole panel for a loading
  // placeholder, which would unmount the composer under the user.
  const activeCaseIdRef = useRef(caseId);
  activeCaseIdRef.current = caseId;
  // The case ref only tracks re-renders, so it goes stale if the panel unmounts
  // mid-flight — which it does on every sidebar tab switch.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Resolves to the answers it stored, or `undefined` when there was no case
  // to fetch or the user had moved on by the time they came back.
  const refreshMedicalMatchData = useCallback(async () => {
    if (!caseId) return undefined;
    const result = await triggerGetMedicalMatch({ caseId }).unwrap();
    // These refreshes outlive the case that started them — the backstop timer
    // and the queue callback both fire long after mount — so a response that
    // arrives once the user has moved on must not land in the new case's panel.
    if (!isMountedRef.current || activeCaseIdRef.current !== caseId) return undefined;
    dispatch(setMedicalMatchData({ caseId, data: result }));
    return result;
  }, [caseId, dispatch, triggerGetMedicalMatch]);

  // Live question flow: POST the question and stop there. A 200 only means the
  // worker job was enqueued, so there's nothing new to fetch yet — the
  // live-processing queue below is what tells us when the answer has landed.
  // `isAsking` covers the POST alone; the queue takes over locking the composer
  // from there.
  const [isAsking, setIsAsking] = useState(false);
  const handleAskQuestion = useCallback(
    async (question: string) => {
      if (!caseId) return false;

      setIsAsking(true);
      try {
        await sendLiveQuestions({ caseId, questions: [question] }).unwrap();
        globalToastRef.current?.show({
          severity: 'success',
          summary: 'Question sent',
          detail: 'Your answer will appear here once the records have been searched.',
          life: 5000,
        });
        return true;
      } catch (error) {
        console.error('Failed to send live Medical Match question:', error);
        // A lock refusal already has its own toast, and the composer keeps the draft
        if (isEndpointLockError(error)) return false;
        globalToastRef.current?.show({
          severity: 'error',
          summary: 'Question not sent',
          detail: getSendLiveQuestionErrorDetail(error, question),
          closable: true,
          life: 5000,
        });
        return false;
      } finally {
        setIsAsking(false);
      }
    },
    [caseId, globalToastRef, sendLiveQuestions],
  );

  // Questions that will never be answered are reported inline above the
  // composer, named and individually dismissible, rather than in a toast: the
  // user has to be able to see which question to ask again after the toast
  // would have gone.
  const liveQuestionsCaseId = canUseLiveMedicalMatch ? caseId : undefined;
  const { failedNotices, isDismissed, dismissNotice, recordFailedQuestions } =
    useLiveQuestionNotices(liveQuestionsCaseId);

  const readLiveQuestionFailures = useLiveQuestionFailures(liveQuestionsCaseId, recordFailedQuestions);

  // The panel's search — see the filter below. Declared up here because the
  // live-answer scroll holds back answers it's hiding. Normalised once and
  // handed to the filter, the highlighter inside each card and that scroll, so
  // what the search keeps and what it marks up can't drift apart.
  const savedSearchTerm = useAppSelector((state) => selectMedicalMatchPanelSearchTerm(state, caseId));
  const [searchTerm, setSearchTerm] = useState(savedSearchTerm);
  const searchQuery = normaliseSearchTerm(searchTerm);

  // A new answer is scrolled to and briefly marked, so the user doesn't have to
  // hunt for where it landed. Declared up here for this hook; the root's parent
  // is the sidebar's scroll container (see the search bar's listener below).
  const rootRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const { flashEntryIds, findAnsweredQuestions, scrollToAnswers } = useLiveAnswerScroll({
    caseId: liveQuestionsCaseId,
    sections: data,
    isLoading,
    searchQuery,
    rootRef,
    searchBarRef,
  });

  // The questions just answered are looked up before the answers are pulled, so
  // every one of them has its entries in what the refresh brings back — and
  // they're only scrolled to once that refresh has put them in the store.
  //
  // It can still come back without them: RTK Query hands a fetch for the same
  // case that's already in flight — the backstop's, or the load on opening the
  // panel — that request's response, which can predate the answer. The queue is
  // usually empty by now, so nothing else would pull again; the refresh is
  // repeated a few times, a short wait apart, until every answer is in.
  const refreshToNewAnswers = useCallback(
    async (lookBackSeconds: number) => {
      const answeredQuestions = await findAnsweredQuestions(lookBackSeconds);

      for (let attempt = 1; ; attempt++) {
        const sections = await refreshMedicalMatchData();
        if (!sections) return;
        const isAllIn = answeredQuestions.every((question) => getAnswerEntryIds(sections, question).length > 0);
        if (isAllIn || attempt >= LIVE_ANSWER_REFRESH_ATTEMPTS) break;
        await new Promise((resolve) => setTimeout(resolve, LIVE_ANSWER_REFRESH_RETRY_DELAY_MS));
        if (!isMountedRef.current || activeCaseIdRef.current !== caseId) return;
      }

      scrollToAnswers(answeredQuestions);
    },
    [caseId, findAnsweredQuestions, refreshMedicalMatchData, scrollToAnswers],
  );

  const handleQueueDrained = useCallback(
    async (lookBackSeconds: number) => {
      // Something left the queue, so the answers are pulled and the failures
      // checked together: whichever of the two the question turned into, the panel
      // shows it on this poll rather than the next one. Deliberately not gated on
      // the failure check — anything that check misses would otherwise hold back
      // an answer that is already there.
      const [, refreshed] = await Promise.allSettled([
        readLiveQuestionFailures(lookBackSeconds),
        refreshToNewAnswers(lookBackSeconds),
      ]);

      if (refreshed.status === 'rejected') {
        handleAnswersRefreshFailed(refreshed.reason);
        // Rethrown so the queue hook holds its baseline back and tries again. The
        // failure check never rejects, so only the answers can ask for a retry.
        throw refreshed.reason;
      }
    },
    [handleAnswersRefreshFailed, readLiveQuestionFailures, refreshToNewAnswers],
  );

  // The queue shrinking means the worker has finished with a question, which is
  // the only reliable signal that there are new answers to fetch. No case means
  // nothing to watch, which is also how the queue stays idle for users without
  // live access.
  const { processingCount, stalledQuestions } = useLiveProcessingQueue(liveQuestionsCaseId, handleQueueDrained);

  // Stalled questions come straight from the queue on every poll, so unlike the
  // failures they need no record of their own — only of the dismissals.
  const liveQuestionNotices: LiveQuestionNotice[] = [
    ...failedNotices,
    ...stalledQuestions
      .filter(({ questionId }) => !isDismissed(questionId))
      .map(({ questionId, question }) => ({ questionId, question, reason: 'STALLED' as const })),
  ];

  // Only runs while the worker has questions in hand. An empty queue can't
  // produce an answer, and a queue that moves triggers its own refresh above.
  const isAnswering = processingCount > 0;
  useEffect(() => {
    if (!caseId || !canUseLiveMedicalMatch || !isAnswering) return;
    const intervalId = setInterval(() => {
      refreshMedicalMatchData().catch(handleAnswersRefreshFailed);
    }, LIVE_ANSWER_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [canUseLiveMedicalMatch, caseId, handleAnswersRefreshFailed, isAnswering, refreshMedicalMatchData]);

  const liveStatusOptions = {
    skip: !caseId || !canUseLiveMedicalMatch,
    pollingInterval: MEDICAL_MATCH_STATUS_POLL_INTERVAL_MS,
    skipPollingIfUnfocused: true,
    refetchOnMountOrArgChange: true,
  };
  const { currentData: liveStatus, isError: liveStatusError } = useGetCanRunLiveMedicalMatchQuery(
    { caseId: caseId ?? '' },
    liveStatusOptions,
  );
  const { currentData: liveStaleness } = useGetLiveMedicalMatchStalenessQuery(
    { caseId: caseId ?? '' },
    liveStatusOptions,
  );

  // Whether a full rerun is available is the backend's call — it also weighs
  // documents still in AI processing and jobs someone else started — so the
  // button mirrors `can-rerun` rather than guessing from what the panel holds.
  const {
    currentData: rerunStatus,
    startedTimeStamp: rerunStatusRequestedAt,
    isFetching: isCheckingRerunStatus,
    isError: rerunStatusError,
  } = useGetCanRerunMedicalMatchQuery(
    { caseId: caseId ?? '' },
    {
      skip: !caseId || !canUseLiveMedicalMatch,
      pollingInterval: MEDICAL_MATCH_STATUS_POLL_INTERVAL_MS,
      skipPollingIfUnfocused: true,
      refetchOnMountOrArgChange: true,
    },
  );
  const isFullRerunRunning = rerunStatus?.reason === MedicalMatchRerunBlockedReason.JOB_IN_PROGRESS;
  const isRerunWaitingOnDocuments =
    rerunStatus?.canRerun === false && rerunStatus.reason === MedicalMatchRerunBlockedReason.DOCUMENTS_PENDING;
  const [rerunPendingSince, setRerunPendingSince] = useState<number | null>(null);
  // The pending flag only bridges the gap until `can-rerun` reports the job, so
  // it stands down while that check is failing — the watcher below can't clear
  // it then, and the composer would stay locked on a rerun that may be long
  // done. `can-run` reports a running rerun by itself in the meantime.
  const isRerunPending = rerunPendingSince !== null && !rerunStatusError;
  const isRerunning = isStartingRerun || isFullRerunRunning || isRerunPending;

  const liveBlockedReason = getLiveBlockedReason({
    isRerunning,
    // Only the can-run check speaks to whether a question can be sent. The
    // staleness banner and the processing queue are informational, so an
    // outage in either must not take the composer away.
    hasCheckError: liveStatusError,
    liveStatus,
    hasCheckedAvailability: Boolean(liveStatus),
  });

  // Holds the notice's re-run link back for a few seconds after a rerun is
  // turned away, so the next click isn't fired straight back at the same no.
  const [isRerunRetryCoolingDown, setIsRerunRetryCoolingDown] = useState(false);
  useEffect(() => {
    if (!isRerunRetryCoolingDown) return;
    const timeoutId = setTimeout(() => setIsRerunRetryCoolingDown(false), MEDICAL_MATCH_RERUN_RETRY_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [isRerunRetryCoolingDown]);

  const handleRerun = useCallback(async () => {
    if (!caseId) return;

    // The mutation refreshes can-run as it settles, which is what swaps the
    // notice above the composer over to the processing one.
    try {
      await rerunMedicalMatch({ caseId }).unwrap();
      setRerunPendingSince(Date.now());
      globalToastRef.current?.show({
        severity: 'success',
        summary: 'Medical Match re-run started',
        detail: MEDICAL_MATCH_RERUN_IN_PROGRESS_MESSAGE,
        life: 5000,
      });
    } catch (error) {
      console.error('Failed to re-run Medical Match:', error);
      setIsRerunRetryCoolingDown(true);
      // A lock refusal already has its own toast
      if (isEndpointLockError(error)) return;
      globalToastRef.current?.show({
        severity: 'error',
        summary: 'Re-run not started',
        detail: getRerunErrorDetail(error),
        closable: true,
        life: 5000,
      });
    }
  }, [caseId, globalToastRef, rerunMedicalMatch]);

  // The notice above the composer follows can-run's reason rather than
  // `canRun`, so it shows whichever way the backend pairs the two. A rerun we
  // know is running stands in for FULL_MEDMATCH_IN_PROGRESS until can-run
  // reports it, so the re-run prompt doesn't linger after its link is used.
  const isFullMedicalMatchProcessing =
    isFullRerunRunning ||
    isRerunPending ||
    liveStatus?.reason === LiveMedicalMatchBlockedReason.FULL_MEDMATCH_IN_PROGRESS;
  const needsRerunForLiveQuestions =
    liveStatus?.reason === LiveMedicalMatchBlockedReason.NO_FULL_MEDMATCH_JOB ||
    liveStatus?.reason === LiveMedicalMatchBlockedReason.CACHE_MISSING;
  // A dismissal holds only while can-run keeps giving one of those reasons. Once
  // the status moves on — a rerun, the index coming back, another case — it's
  // dropped, so the prompt shows again if the reason returns.
  const [isRerunPromptDismissed, setIsRerunPromptDismissed] = useState(false);
  useEffect(() => {
    if (!needsRerunForLiveQuestions) setIsRerunPromptDismissed(false);
  }, [needsRerunForLiveQuestions]);
  const isRerunPromptVisible = !isFullMedicalMatchProcessing && !isRerunPromptDismissed && needsRerunForLiveQuestions;

  const wasFullRerunRunningRef = useRef(false);
  useEffect(() => {
    if (!rerunStatus || isCheckingRerunStatus || rerunStatusError) return;
    if (isFullRerunRunning) {
      wasFullRerunRunningRef.current = true;
      return;
    }
    if (rerunPendingSince !== null && (rerunStatusRequestedAt ?? 0) <= rerunPendingSince) return;

    if (wasFullRerunRunningRef.current || rerunPendingSince !== null) {
      wasFullRerunRunningRef.current = false;
      setRerunPendingSince(null);
      // A finished rerun rebuilds the live index, so whether questions can be
      // asked and whether the answers cover every record are re-checked too.
      // Only here: a live answer landing changes neither.
      refreshMedicalMatchData()
        .then((sections) => {
          if (sections) dispatch(api.util.invalidateTags([{ type: MEDICAL_MATCH_LIVE_STATUS_TAG, id: caseId }]));
        })
        .catch(handleAnswersRefreshFailed);
    }
  }, [
    caseId,
    dispatch,
    handleAnswersRefreshFailed,
    isCheckingRerunStatus,
    isFullRerunRunning,
    refreshMedicalMatchData,
    rerunPendingSince,
    rerunStatus,
    rerunStatusError,
    rerunStatusRequestedAt,
  ]);

  const getPageNumber = useCallback(
    (pageId: string) => {
      const idx = getFullBundlePageIndexByPageId(pageId);
      return idx >= 0 ? idx + 1 : -1;
    },
    [getFullBundlePageIndexByPageId],
  );

  // Session-only "visited chips" set — lives in Redux (see medicalMatch
  // slice) so it survives the panel unmounting when the user switches
  // sidebar tabs. Keyed by pageId so the same reference appearing across
  // multiple entry blocks stays consistently marked. Not persisted beyond
  // the tab session — clears on refresh, matching browser visited-link
  // behaviour and avoiding the need for a "clear" control.
  const visitedCitationPageIdsList = useAppSelector(selectMedicalMatchVisitedCitationPageIds);
  const visitedPageIds = useMemo(() => new Set(visitedCitationPageIdsList), [visitedCitationPageIdsList]);

  const handlePageNumberClick = useCallback(
    (pageId: string, snippets: string[]) => {
      dispatch(setMedicalMatchHighlight({ pageId, texts: snippets }));
      dispatch(addVisitedCitationPageId(pageId));

      const pageIndex = getBundlePageIndexByPageId(pageId);
      if (pageIndex !== -1) {
        documentReviewPageContext.onScrollToPageIndexInReactVirtualizedViewport(pageIndex);
      }
    },
    [getBundlePageIndexByPageId, documentReviewPageContext, dispatch],
  );

  // FE-only search over question, answer and breach text. Case-insensitive
  // substring match; empty term shows the full unfiltered panel.
  //
  // Every entry and breach is judged on its own, so only the ones that match
  // survive and a section disappears once neither has anything left. Headings
  // and allegations deliberately don't keep a section alive — a word shared
  // with an allegation would otherwise hand back that whole section, which
  // reads as the filter having given up. They are still marked up when they
  // happen to contain the term.
  //
  // Citation snippets are out of scope for the same reason: they only appear
  // inside a reference tooltip, so a card matched on one would come back with
  // no visible match on it.
  const filteredData = useMemo<MedicalMatchSection[]>(() => {
    const occurrences = new Map<string, number>();
    const sections = data.map((section) => {
      const heading = JSON.stringify([section.title, section.allegation ?? null]);
      const occurrence = occurrences.get(heading) ?? 0;
      occurrences.set(heading, occurrence + 1);
      return { ...section, sectionKey: `${heading}#${occurrence}` };
    });
    if (!searchQuery) return sections;
    return sections
      .map((section) => ({
        ...section,
        entries: section.entries.filter(
          (entry) => matchesSearchTerm(entry.question, searchQuery) || matchesSearchTerm(entry.answer, searchQuery),
        ),
        breaches: section.breaches.filter((breach) => matchesSearchTerm(breach.breach, searchQuery)),
      }))
      .filter((section) => section.entries.length > 0 || section.breaches.length > 0);
  }, [data, searchQuery]);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);
  const handleSearchClear = useCallback(() => setSearchTerm(''), []);

  // The search and how far down the user had scrolled are handed to the store
  // as the panel unmounts — on every sidebar tab switch — and put back when it
  // reopens on the same case. A layout effect so the position is read before
  // the next tab's content takes this panel's place in the shared scroll
  // container, and restored before the search bar's listener below takes its
  // starting point from it. Only restored when the answers were already in the
  // store: otherwise there's nothing on screen yet to scroll down.
  const savedScrollTop = useAppSelector((state) => selectMedicalMatchPanelScrollTop(state, caseId));
  const savedScrollTopRef = useRef(savedScrollTop);
  savedScrollTopRef.current = savedScrollTop;
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;
  useLayoutEffect(() => {
    const scrollEl = rootRef.current?.parentElement;
    if (!caseId || !scrollEl) return;
    if (fetchedCaseIdRef.current === caseId) scrollEl.scrollTop = savedScrollTopRef.current;

    return () => {
      dispatch(updateMedicalMatchPanel({ caseId, searchTerm: searchTermRef.current, scrollTop: scrollEl.scrollTop }));
    };
  }, [caseId, dispatch]);

  // Scroll-direction reveal for the search bar.
  //
  // The scroll container is the panel's parent (`overflow-auto` wrapper from
  // BundleSidebarTabs), not this component's root — so we attach the listener
  // to `rootRef.current.parentElement` rather than `window`.
  //
  // Behaviour:
  //   - Scrolling DOWN past a small threshold hides the bar (translated up).
  //   - Any UP-scroll immediately reveals it — no need to hit the top.
  //   - When scrolled at the very top, the bar is always shown.
  const [isSearchVisible, setIsSearchVisible] = useState(true);

  // Only bound once there are answers: with nothing to scroll there's nothing
  // for the bar to get out of the way of. Re-run when `data` arrives so the
  // listener is in place from the first answer on.
  useLayoutEffect(() => {
    if (!data || data.length === 0) return;
    const scrollEl = rootRef.current?.parentElement;
    if (!scrollEl) return;

    // Ignore sub-4px twitches so trackpad noise / horizontal-only movements
    // don't strobe the bar. 4px is a couple of scroll ticks on most inputs.
    const DELTA_THRESHOLD = 4;
    // Below this scrollTop we always show the bar — feels less abrupt than
    // hiding immediately when the user is barely off the top.
    const REVEAL_AT_TOP_THRESHOLD = 8;

    let lastScrollTop = scrollEl.scrollTop;

    const handleScroll = () => {
      const currentScrollTop = scrollEl.scrollTop;
      const delta = currentScrollTop - lastScrollTop;

      if (currentScrollTop <= REVEAL_AT_TOP_THRESHOLD) {
        setIsSearchVisible(true);
      } else if (delta > DELTA_THRESHOLD) {
        setIsSearchVisible(false);
      } else if (delta < -DELTA_THRESHOLD) {
        setIsSearchVisible(true);
      }

      lastScrollTop = currentScrollTop;
    };

    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [data]);

  // Serialise every currently-visible section's title / allegation / Q&A /
  // breach content into a single plain-text blob and push it to the OS
  // clipboard. Wired up to the header copy button; previously lived on a
  // right-click context menu.
  //
  // Reads `filteredData` rather than the raw `data` so that when the search
  // bar is narrowing the panel, the copied output matches what the user can
  // actually see — no need to clear the search first to get a scoped copy.
  // Empty search → every section, so behaviour is unchanged.
  //
  // A refused write (no clipboard permission, or not a secure context) gets a
  // toast rather than nothing. After a successful write we flip `copiedAll` to
  // swap the icon for a green tick for 1.6s. The timer lives on a ref so a fast second click
  // cancels the pending revert and starts a fresh window, keeping the tick
  // visible instead of flickering back to the copy icon mid-animation.
  const [copiedAll, setCopiedAll] = useState(false);
  const copiedResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copiedResetTimerRef.current) clearTimeout(copiedResetTimerRef.current);
    },
    [],
  );
  const handleCopyAll = useCallback(() => {
    if (!filteredData) return;
    const text = filteredData
      .map((section) => {
        const parts: string[] = [section.title];
        if (section.allegation) parts.push(section.allegation);
        for (const entry of section.entries) {
          parts.push(`${entry.question}\n${entry.answer}`);
        }
        for (const breach of section.breaches) {
          parts.push(`Potential breach\n${breach.breach}`);
        }
        return parts.join('\n\n');
      })
      .join('\n\n');
    writeToClipboard(text)
      .then(() => {
        setCopiedAll(true);
        if (copiedResetTimerRef.current) clearTimeout(copiedResetTimerRef.current);
        copiedResetTimerRef.current = setTimeout(() => setCopiedAll(false), 1600);
      })
      .catch((error) => {
        console.error('Failed to copy Medical Match answers:', error);
        globalToastRef.current?.show({
          severity: 'error',
          summary: MEDICAL_MATCH_COPY_FAILED_SUMMARY,
          detail: MEDICAL_MATCH_COPY_FAILED_DETAIL,
          closable: true,
          life: 5000,
        });
      });
  }, [filteredData, globalToastRef]);

  const hasResults = filteredData.length > 0;

  return (
    // `min-h-full` so the sticky composer sits at the bottom of the scroll area
    // (via its own `mt-auto`) even when there are only a couple of answers.
    // The panel is a vertical scroller only: `overflow-x-clip` stops a stray
    // hover tooltip (they're laid out even while hidden) from handing the
    // sidebar a horizontal scrollbar. It's `clip` rather than `hidden` because
    // `hidden` would make this element the scroll container and break the
    // sticky search bar and composer. Anything that genuinely needs to escape
    // the panel's width should portal out, as the reference chips do.
    <div ref={rootRef} className="flex min-h-full flex-col gap-6 overflow-x-clip px-[24px] pb-[24px] font-sans">
      {/* FE-only Q/A filter — narrows the list to sections whose entries
            match the term. `w-full` overrides InputSearch's default fixed
            width so it fills the panel.
            Sticky reveal: pinned to the top of the scroll container so it
            stays reachable while browsing. `translate-y` toggles it off-
            screen on scroll-down and back into view on scroll-up (see the
            scroll listener above). `-mx-[24px] px-[24px]` breaks it out of
            the panel's own padding so the background wash reaches the panel
            edges when scrolled content sits behind it; matches the sidebar
            wrapper's `bg-fletchers-gray-50` so no colour seam is visible.
            No vertical padding of its own — the panel root's `p-[24px]`
            already provides space above the search when at rest, and when
            stuck the entries scroll behind it. `z-10` keeps it above them. */}
      <div
        ref={searchBarRef}
        className={classNames(
          'bg-fletchers-gray-50 sticky top-0 z-10 -mx-[24px] -mb-[16px] px-[24px] pt-[24px] pb-[16px] transition-transform duration-200 ease-out',
          isSearchVisible ? 'translate-y-0' : '-translate-y-full',
        )}
      >
        <div className="flex items-center gap-2">
          <InputSearch
            className="!w-full flex-1"
            disabled={isLoading || Boolean(error) || !data.length}
            placeholder="Search questions and answers"
            value={searchTerm}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
          />
          {/* Right-click "Copy all" was replaced with a visible icon
                button; matches the per-entry copy button styling on each
                MedicalMatchEntryBlock. `title` produces the native browser
                tooltip on hover; `aria-label` covers screen readers.
                On successful copy, the copy icon swaps for a fletchers-
                green tick with a `copy-success-pop` scale animation
                (see index.css) for 1.6s. `key` on the icon forces a fresh
                mount each time `copiedAll` flips so the animation re-runs. */}
          <OutlineIconButton
            icon={
              copiedAll ? (
                <CheckIconV2
                  key="copied"
                  className="text-fletchers-green-600"
                  style={{ animation: 'copy-success-pop 300ms cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}
                />
              ) : (
                <CopyIcon key="copy" />
              )
            }
            onClick={handleCopyAll}
            disabled={isLoading || Boolean(error) || !hasResults}
            title={copiedAll ? 'Copied' : 'Copy all to clipboard'}
            aria-label={copiedAll ? 'Copied' : 'Copy all to clipboard'}
            className="!bg-white"
          />
          {canUseLiveMedicalMatch && (
            <MedicalMatchRerunButton
              canRerun={Boolean(rerunStatus?.canRerun) && !rerunStatusError}
              tooltip={
                isRerunning
                  ? MEDICAL_MATCH_RERUN_IN_PROGRESS_MESSAGE
                  : getRerunTooltip(rerunStatus?.canRerun, rerunStatus?.reason)
              }
              isRerunning={isRerunning}
              onRerun={handleRerun}
            />
          )}
        </div>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-gray-500">Loading Medical Match data...</div>
      ) : error ? (
        <div role="alert" className="text-fletchers-red-600 flex items-center justify-center py-6">
          Error loading Medical Match data
        </div>
      ) : hasResults ? (
        filteredData.map((section) => (
          <Section
            key={section.sectionKey}
            title={section.title}
            allegation={section.allegation}
            entries={section.entries}
            breaches={section.breaches}
            onPageNumberClick={handlePageNumberClick}
            getPageNumber={getPageNumber}
            visitedPageIds={visitedPageIds}
            flashEntryIds={flashEntryIds}
            searchQuery={searchQuery}
          />
        ))
      ) : (
        <div className="flex items-center justify-center py-6">
          <span className="text-gray-400">{data.length ? 'No matches' : 'No data available'}</span>
        </div>
      )}
      {/* Ad-hoc question composer, pinned to the bottom of the scroll
            container so it stays reachable while browsing the answers. Mirrors
            the search bar's `-mx/-mb + px/pb` trick so the background wash
            reaches the panel edges and the composer can sit flush with the
            bottom of the scroll area instead of the panel's own padding. */}
      {canUseLiveMedicalMatch && (
        <div className="bg-fletchers-gray-50 sticky bottom-0 z-10 -mx-[24px] mt-auto -mb-[24px] px-[24px] pt-[16px] pb-[24px]">
          <MedicalMatchProcessingBar
            isProcessing={processingCount > 0}
            notices={liveQuestionNotices}
            onDismissNotice={dismissNotice}
          />
          {isFullMedicalMatchProcessing && (
            <LiveQuestionsNotice
              tone="info"
              icon={
                <span
                  aria-hidden
                  className="border-fletchers-green-100 border-t-fletchers-green-600 mt-0.5 h-4 w-4 shrink-0 animate-spin rounded-full border-2"
                />
              }
              message={MEDICAL_MATCH_RERUN_IN_PROGRESS_MESSAGE}
            />
          )}
          {/* Deliberately not gated on can-rerun: when live questions aren't
              on yet, a rerun is the way to turn them on. */}
          {isRerunPromptVisible && (
            <LiveQuestionsNotice
              tone="info"
              message={
                <>
                  {LIVE_MEDICAL_MATCH_NOT_ENABLED_MESSAGE}{' '}
                  <button
                    type="button"
                    onClick={handleRerun}
                    disabled={isStartingRerun || isRerunRetryCoolingDown}
                    className="text-fletchers-green-600 cursor-pointer font-semibold underline underline-offset-2 disabled:cursor-default disabled:opacity-60"
                  >
                    {LIVE_MEDICAL_MATCH_RERUN_LINK_LABEL}
                  </button>{' '}
                  {LIVE_MEDICAL_MATCH_RERUN_PROMPT}
                </>
              }
              onDismiss={() => setIsRerunPromptDismissed(true)}
            />
          )}
          {/* Stated up front rather than left to the Ask button's hover, as
              it holds for the rest of the day. Not dismissible: it stays until
              can-run reports something else. */}
          {liveStatus?.reason === LiveMedicalMatchBlockedReason.DAILY_LIMIT_REACHED && (
            <LiveQuestionsNotice tone="info" message={LIVE_QUESTION_DAILY_LIMIT_MESSAGE} />
          )}
          {liveStaleness?.isStale && (
            <LiveQuestionsNotice
              icon={<InfoCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />}
              message={
                isRerunWaitingOnDocuments
                  ? LIVE_MEDICAL_MATCH_STALE_DOCUMENTS_PENDING_MESSAGE
                  : LIVE_MEDICAL_MATCH_STALE_MESSAGE
              }
            />
          )}
          {/* Keyed so a draft typed on one case never carries into the next. */}
          <MedicalMatchQuestionComposer
            key={caseId}
            caseId={caseId}
            onAsk={handleAskQuestion}
            isAsking={isAsking}
            blockedReason={liveBlockedReason}
          />
        </div>
      )}
    </div>
  );
}

type LiveQuestionsNoticeProps = {
  message: ReactNode;
  /**
   * Yellow for a limit or gap the user should know about, green for where
   * Medical Match itself has got to.
   */
  tone?: 'warning' | 'info';
  icon?: ReactNode;
  /** Adds a close button when given. */
  onDismiss?: () => void;
};

/** Notice above the composer about the state of live questions. */
function LiveQuestionsNotice({ message, tone = 'warning', icon, onDismiss }: LiveQuestionsNoticeProps) {
  const isInfo = tone === 'info';
  return (
    <div
      role="status"
      className={classNames(
        'text-fletchers-black mb-3 flex items-start gap-2 rounded-md p-3 text-[14px]',
        isInfo ? 'border-fletchers-green-100 bg-fletchers-green-50/50 border' : 'bg-fletchers-yellow-500/10',
      )}
    >
      {icon}
      <span className="min-w-0 flex-1">{message}</span>
      {onDismiss && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className={classNames(
            'flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded transition-colors focus-visible:outline-2',
            isInfo
              ? 'hover:bg-fletchers-green-100 focus-visible:outline-fletchers-green-600'
              : 'hover:bg-fletchers-yellow-500/20 focus-visible:outline-fletchers-yellow-500',
          )}
        >
          <CloseIcon aria-hidden width={12} height={12} />
        </button>
      )}
    </div>
  );
}

const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="13" viewBox="0 0 16 13" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.35267 1.25932L14.4587 10.1953C15.052 11.234 14.302 12.5266 13.106 12.5266H2.894C1.69733 12.5266 0.947334 11.234 1.54133 10.1953L6.64733 1.25932C7.24533 0.211983 8.75467 0.211983 9.35267 1.25932Z"
      fill="#FFC96C"
      stroke="#FFC96C"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.99933 9.24998C7.90733 9.24998 7.83267 9.32465 7.83333 9.41665C7.83333 9.50865 7.908 9.58331 8 9.58331C8.092 9.58331 8.16667 9.50865 8.16667 9.41665C8.16667 9.32465 8.092 9.24998 7.99933 9.24998"
      stroke="#323232"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M8 7.24669V4.75336" stroke="#323232" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type SectionProps = {
  title: string;
  allegation?: string;
  entries: MedicalMatchEntry[];
  breaches: MedicalMatchBreach[];
  onPageNumberClick: (pageId: string, snippets: string[]) => void;
  getPageNumber: (pageId: string) => number;
  visitedPageIds: ReadonlySet<string>;
  flashEntryIds: ReadonlySet<string>;
  searchQuery: string;
};

function Section({
  title,
  allegation,
  entries,
  breaches,
  onPageNumberClick,
  getPageNumber,
  visitedPageIds,
  flashEntryIds,
  searchQuery,
}: SectionProps) {
  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-fletchers-black text-[14px] font-semibold">
        <HighlightedText text={title} searchQuery={searchQuery} />
      </h2>
      {allegation ? (
        <p className="font-sans text-[14px] leading-[normal] font-normal text-[#000] not-italic">
          <HighlightedText text={allegation} searchQuery={searchQuery} />
        </p>
      ) : null}
      {entries.map((entry) => (
        <MedicalMatchEntryBlock
          key={entry.entryId}
          entry={entry}
          onPageNumberClick={onPageNumberClick}
          getPageNumber={getPageNumber}
          visitedPageIds={visitedPageIds}
          isFlashing={flashEntryIds.has(entry.entryId)}
          searchQuery={searchQuery}
        />
      ))}
      {breaches.map((breach, index) => (
        <div key={index} className="rounded-lg border border-gray-200 bg-[#FFF7EA] p-[24px]">
          <div className="flex items-center gap-2">
            <WarningIcon />
            <p className="text-[14px] font-semibold text-gray-900">Potential breach</p>
          </div>
          <p className="mt-2 font-sans text-[14px] leading-[normal] font-normal text-[#000] not-italic">
            <HighlightedText text={breach.breach} searchQuery={searchQuery} />
          </p>
        </div>
      ))}
    </section>
  );
}
